import { useCallback, useEffect, useRef, useState } from 'react';
import useHorizontalOverflow from './useHorizontalOverflow';
import useLinkedScrollLeft from './useLinkedScrollLeft';
import useFollowScrollLeft from './useFollowScrollLeft';
import useDragScroll from './useDragScroll';
import { readEdgeFade, edgeFadeMask } from '../components/ui/edgeFade';

/**
 * `useGanttScrollFrame(timelineRef, { fade })` — THE horizontal wiring of a
 * page-scroll Gantt: an x-only TIMELINE scroller with its date band pinned
 * OUTSIDE it (so the band can pin to the page) and a sticky bottom bar. Shared
 * by the Crew Gantt (`budget/CrewGanttView.js`, `pageScroll`), the hires Gantt
 * (`hires/GanttView.js`), the cross-team Gantt (`teams/CrossTeamGanttView.js`,
 * `pageScroll`) and the Labour Gantt (`teams/LabourGanttView.js`). Extracted
 * 2026-10-05 from the crew chart (owner: *"check all gannt charts for all the
 * updates we are making. should be common code/fixes where possible"*), moved
 * not rewritten — the crew chart's DOM and behaviour are unchanged.
 *
 * Returns `{ stickyBarRef, overflows, scrollWidth, fadeStyle, readFade,
 * dateBandRef, dateAxisClipRef, handleBandWheel }`:
 *   - `stickyBarRef` — give it to a `ui/StickyScrollbar`, rendered only while
 *     `overflows` (`hooks/useHorizontalOverflow`), its spacer `scrollWidth`
 *     wide; it is linked TWO-WAY with the timeline (`useLinkedScrollLeft`).
 *   - `fadeStyle` — THE `ui/edgeFade` mask, for the timeline AND the band's
 *     clip pane so header and body fade together; `undefined` when nothing is
 *     hidden or `fade` is off. Re-read on resize and on overflow changes; the
 *     caller ALSO calls `readFade` from the timeline's `onScroll`.
 *   - `dateBandRef` / `dateAxisClipRef` / `handleBandWheel` — the band OUTSIDE
 *     the timeline: its clip follows the timeline ONE way
 *     (`useFollowScrollLeft`), a drag on the band pans the TIMELINE
 *     (`useDragScroll`'s `scrollTargetRef`), and a `deltaX` wheel on the band
 *     is forwarded. 🚫 Refs only, never `getElementById` — two charts can be
 *     mounted at once.
 *
 * 🪤 A sticky band is INERT inside an x-only scroller (a scroller is a scroll
 * container on BOTH axes), which is why the band sits outside the timeline. And
 * NO ANCESTOR may be `overflow-hidden` — it becomes the scroll container both
 * stickies pin to; clip with `overflow-clip`.
 * jsdom has no layout: whether anything pins, fades or lines up is a BROWSER
 * check.
 */
export default function useGanttScrollFrame(timelineRef, { fade = true } = {}) {
  const stickyBarRef = useRef(null);
  const { overflows, scrollWidth } = useHorizontalOverflow(timelineRef);
  useLinkedScrollLeft([timelineRef, stickyBarRef]);

  const [edgeFade, setEdgeFade] = useState({ left: false, right: false });
  const readFade = useCallback(() => {
    if (!fade) return;
    const next = readEdgeFade(timelineRef.current);
    setEdgeFade((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, [fade, timelineRef]);
  useEffect(() => {
    readFade();
    window.addEventListener('resize', readFade);
    return () => window.removeEventListener('resize', readFade);
  }, [readFade, scrollWidth, overflows]);
  const fadeStyle = fade && (edgeFade.left || edgeFade.right)
    ? { WebkitMaskImage: edgeFadeMask(edgeFade), maskImage: edgeFadeMask(edgeFade) }
    : undefined;

  const dateBandRef = useRef(null);
  const dateAxisClipRef = useRef(null);
  useFollowScrollLeft(timelineRef, dateAxisClipRef);
  useDragScroll(dateBandRef, { axis: 'x', scrollTargetRef: timelineRef });
  const handleBandWheel = useCallback((e) => {
    const timeline = timelineRef.current;
    if (!timeline || !e.deltaX || Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    timeline.scrollLeft += e.deltaX;
  }, [timelineRef]);

  return {
    stickyBarRef, overflows, scrollWidth, fadeStyle, readFade, dateBandRef, dateAxisClipRef, handleBandWheel,
  };
}
